import React, { useState } from 'react';
import { Send, Phone, Mail, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import contactData from '../data/contact.json';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission - in real app, this would send to backend
    toast.success('Nachricht gesendet!', {
      description: 'Wir melden uns so schnell wie möglich bei Ihnen.',
      duration: 5000
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="pt-20">
      <Toaster position="top-right" richColors />
      <SEO 
        title="Kontakt - Ali Baba Kebab & Vegie Sarnen | Reservierung & Anfragen"
        description="Kontaktieren Sie Ali Baba Kebab & Vegie in Sarnen. Telefon: 041 660 29 29, E-Mail: info@alibaba-sarnen.ch, Adresse: Grundacherweg 1, 6060 Sarnen. Reservierungen willkommen!"
        keywords="Kebab Kontakt Sarnen, Restaurant Reservierung Sarnen, Telefon Takeaway Sarnen"
        path="/contact"
      />
      
      {/* Hero */}
      <section className="section bg-[#1C1917] text-center">
        <div className="container mx-auto">
          <h1 className="text-6xl md:text-7xl text-white mb-6">KONTAKT</h1>
          <p className="text-xl text-[#F7E39A] max-w-3xl mx-auto">
            Haben Sie Fragen oder möchten Sie eine Reservierung vornehmen? Kontaktieren Sie uns!
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section bg-[#0F0E0D]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-[#1C1917] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.3)' }}>
              <h2 className="text-3xl text-[#F7E39A] mb-6">Nachricht senden</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white mb-2 font-medium">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2725] text-white border border-[#F7E39A]/30 focus:border-[#F7E39A] focus:outline-none transition-colors"
                    placeholder="Ihr Name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white mb-2 font-medium">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2725] text-white border border-[#F7E39A]/30 focus:border-[#F7E39A] focus:outline-none transition-colors"
                    placeholder="ihre@email.ch"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-white mb-2 font-medium">
                    Telefon (optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2725] text-white border border-[#F7E39A]/30 focus:border-[#F7E39A] focus:outline-none transition-colors"
                    placeholder="041 123 45 67"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-white mb-2 font-medium">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2725] text-white border border-[#F7E39A]/30 focus:border-[#F7E39A] focus:outline-none transition-colors resize-none"
                    placeholder="Ihre Nachricht an uns..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#F7E39A] text-[#1C1917] px-8 py-4 rounded-lg font-semibold text-lg flex items-center justify-center space-x-2 transition-transform hover:scale-105"
                >
                  <Send size={20} />
                  <span>Nachricht senden</span>
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-[#1C1917] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.3)' }}>
                <div className="flex items-start space-x-4 mb-6">
                  <Phone className="text-[#F7E39A] flex-shrink-0" size={32} />
                  <div>
                    <h3 className="text-2xl text-[#F7E39A] mb-2">Telefon</h3>
                    <a href={`tel:${contactData.restaurant.phone}`} className="text-white text-xl hover:text-[#F7E39A] transition-colors">
                      {contactData.restaurant.phoneDisplay}
                    </a>
                    <p className="text-[#E3E3E3] mt-2">{contactData.hours.weekdays.days}:<br />{contactData.hours.weekdays.lunch.open} - {contactData.hours.weekdays.lunch.close}<br />{contactData.hours.weekdays.dinner.open} - {contactData.hours.weekdays.dinner.close}<br /><br />{contactData.hours.weekend.days}: {contactData.hours.weekend.open} - {contactData.hours.weekend.close}</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1C1917] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.3)' }}>
                <div className="flex items-start space-x-4 mb-6">
                  <Mail className="text-[#F7E39A] flex-shrink-0" size={32} />
                  <div>
                    <h3 className="text-2xl text-[#F7E39A] mb-2">E-Mail</h3>
                    <a href={`mailto:${contactData.restaurant.email}`} className="text-white text-xl hover:text-[#F7E39A] transition-colors">
                      {contactData.restaurant.email}
                    </a>
                    <p className="text-[#E3E3E3] mt-2">Wir antworten innerhalb von 24 Stunden</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1C1917] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.3)' }}>
                <div className="flex items-start space-x-4 mb-6">
                  <MapPin className="text-[#F7E39A] flex-shrink-0" size={32} />
                  <div>
                    <h3 className="text-2xl text-[#F7E39A] mb-2">Adresse</h3>
                    <p className="text-white text-lg">{contactData.restaurant.address.street}<br />{contactData.restaurant.address.zip} {contactData.restaurant.address.city}, {contactData.restaurant.address.country}</p>
                    <a
                      href={contactData.social.google}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#F7E39A] hover:text-white transition-colors mt-2 inline-block"
                    >
                      Route planen →
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#F7E39A] p-8 rounded-lg text-[#1C1917]">
                <h3 className="text-2xl font-bold mb-3">Reservierung empfohlen</h3>
                <p className="text-lg">
                  Für grössere Gruppen empfehlen wir eine telefonische Reservierung. So können wir Ihnen den besten Service bieten!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
